import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaCloud, FaRobot, FaDatabase, FaShieldAlt, FaChartLine,
  FaUsers, FaHeadset, FaCogs, FaCheckCircle, FaArrowRight,
  FaBrain, FaMobileAlt, FaServer, FaLock, FaGlobe, FaTools
} from 'react-icons/fa';

const Capabilities = () => {
  const [activeTab, setActiveTab] = useState('crm-solutions');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const tabs = [
    { id: 'crm-solutions', label: 'CRM Solutions', icon: FaCloud },
    { id: 'salesforce', label: 'Salesforce', icon: FaUsers },
    { id: 'ai-automation', label: 'AI & Automation', icon: FaRobot },
    { id: 'integrations', label: 'Integrations', icon: FaCogs },
  ];

  const technologies = {
    'crm-solutions': {
      title: 'Enterprise CRM Solutions',
      description: 'Comprehensive CRM platforms designed for modern businesses',
      systems: [
        {
          icon: FaCloud,
          title: 'Custom CRM Development',
          description: 'Tailor-made CRM solutions built specifically for your business processes and requirements.',
          features: [
            'Fully customizable to your workflows',
            'Scalable architecture for growth',
            'Intuitive user interface design',
            'Mobile-first responsive design',
            'Multi-language and multi-currency support',
          ],
        },
        {
          icon: FaServer,
          title: 'Cloud-Based CRM',
          description: 'Secure, scalable cloud CRM with 99.9% uptime and global accessibility.',
          features: [
            'AWS/Azure/GCP deployment options',
            'Automatic backups and updates',
            'Global data center availability',
            'GDPR and data residency compliance',
            '99.9% SLA guarantee',
          ],
        },
      ],
    },
    'salesforce': {
      title: 'Salesforce Solutions',
      description: 'Expert Salesforce implementation and optimization services',
      systems: [
        {
          icon: FaUsers,
          title: 'Sales Cloud',
          description: 'Complete sales management solution to track leads, opportunities, and close deals faster.',
          features: [
            'Lead and opportunity management',
            'Sales forecasting and analytics',
            'Account and contact management',
            'Workflow automation and approvals',
            'Mobile sales force automation',
          ],
        },
        {
          icon: FaHeadset,
          title: 'Service Cloud',
          description: 'Enterprise customer service platform for omnichannel support and case management.',
          features: [
            'Omnichannel case routing',
            'Knowledge base and self-service portal',
            'AI-powered chatbots and automation',
            'SLA management and escalations',
            'Customer satisfaction tracking',
          ],
        },
      ],
    },
    'ai-automation': {
      title: 'AI & Intelligent Automation',
      description: 'Leverage artificial intelligence to automate and optimize business processes',
      systems: [
        {
          icon: FaBrain,
          title: 'AI-Powered Analytics',
          description: 'Predictive analytics and business intelligence with machine learning insights.',
          features: [
            'Sales forecasting and pipeline analysis',
            'Customer churn prediction',
            'Personalized product recommendations',
            'Sentiment analysis on customer feedback',
            'Automated reporting and dashboards',
          ],
        },
        {
          icon: FaRobot,
          title: 'Process Automation',
          description: 'Intelligent automation for repetitive tasks and complex workflows.',
          features: [
            'RPA for data entry and processing',
            'Automated email and notification workflows',
            'Smart document processing and OCR',
            'Approval routing automation',
            'Integration with 500+ apps via Zapier/Make',
          ],
        },
      ],
    },
    'integrations': {
      title: 'Seamless Integrations',
      description: 'Connect your CRM with the tools you already use',
      systems: [
        {
          icon: FaTools,
          title: 'ERP Integration',
          description: 'Seamless two-way sync between CRM and ERP systems including SAP, Oracle, Microsoft Dynamics.',
          features: [
            'Real-time inventory and order sync',
            'Customer and product data consistency',
            'Invoice and payment synchronization',
            'Automated quote-to-cash workflow',
            'Bidirectional data validation',
          ],
        },
        {
          icon: FaMobileAlt,
          title: 'Marketing & Communication',
          description: 'Integrate with email, SMS, social media, and marketing automation platforms.',
          features: [
            'Mailchimp, SendGrid, and HubSpot integration',
            'SMS and WhatsApp business API',
            'Social media listening and engagement',
            'Marketing campaign tracking',
            'Lead scoring and nurturing automation',
          ],
        },
      ],
    },
  };

  const currentTech = technologies[activeTab];

  return (
    <div className="min-h-screen">
      <section className="relative py-24 bg-primary-950 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
        
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-accent/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-3 mb-6">
              <FaCogs className="text-accent text-2xl" />
              <span className="text-accent font-display text-sm tracking-[0.3em]">CRM TECHNOLOGY SOLUTIONS</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
              Enterprise CRM
              <span className="text-accent block">Solutions That Drive Growth</span>
            </h1>
            
            <div className="golden-line mx-auto mt-8" />
          </motion.div>
        </div>
      </section>

      <section className="relative py-20 bg-primary-900" ref={ref}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="flex flex-wrap justify-center gap-3 mb-16"
          >
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 font-display text-sm tracking-wider transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-accent text-primary-950 shadow-lg shadow-accent/25'
                    : 'border border-gray-700 text-gray-400 hover:border-accent/50 hover:text-accent'
                }`}
              >
                <tab.icon />
                <span>{tab.label}</span>
              </motion.button>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
                  {currentTech.title}
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  {currentTech.description}
                </p>
                <div className="golden-line mx-auto mt-6" />
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {currentTech.systems.map((system, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    whileHover={{ y: -5 }}
                    className="glass-card p-8 group"
                  >
                    <div className="w-14 h-14 bg-accent/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-all">
                      <system.icon className="text-2xl text-accent" />
                    </div>
                    
                    <h3 className="text-xl font-display font-bold text-white mb-4">
                      {system.title}
                    </h3>
                    
                    <p className="text-gray-400 mb-6 leading-relaxed">
                      {system.description}
                    </p>

                    <div className="space-y-3 mb-6">
                      {system.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <FaCheckCircle className="text-accent text-sm flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-accent to-transparent 
                                  transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <section className="relative py-20 bg-primary-950">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-display text-sm tracking-[0.3em]">WHY SRONZER</span>
            <h2 className="section-title mt-4">
              We Deliver <span className="text-accent">Excellence</span>
            </h2>
            <div className="golden-line mx-auto mt-6" />
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: FaUsers, title: 'Expert Team', description: '25+ skilled developers and CRM specialists ready to deliver any solution you need.' },
              { icon: FaChartLine, title: 'Proven Results', description: '98% client satisfaction rate with measurable ROI improvements.' },
              { icon: FaHeadset, title: '24/7 Support', description: 'Round-the-clock technical support and dedicated account management.' },
              { icon: FaLock, title: 'Enterprise Security', description: '' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-6 text-center"
              >
                <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-2xl text-accent" />
                </div>
                <h3 className="text-lg font-display font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="glass-card p-10 text-center"
          >
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              Our platform integrates seamlessly with Salesforce, HubSpot, Zoho, and 500+ other tools. 
              Whether you need custom development, Salesforce optimization, or AI-powered automation, 
              we have the expertise to deliver exactly what your business needs.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary group flex items-center space-x-2 mx-auto mt-8"
            >
              <span>SCHEDULE A DEMO</span>
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Capabilities;